/**
 * COMMERCE-LAUNCH-167A-R10 — shared customer catalog sort contract.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

export type CatalogSortKey =
  | "recommended"
  | "newest"
  | "price_asc"
  | "price_desc"
  | "name_asc"
  | "brand"
  | "stock_first";

/** Legacy SortKey aliases used by CatalogListV2 before R10. */
export type LegacySortKey = "default" | "price_asc" | "price_desc" | "name" | "brand" | "stock";

export function normalizeCatalogSortKey(raw: string | null | undefined): CatalogSortKey {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "default" || s === "recommended") return "recommended";
  if (s === "newest" || s === "new" || s === "new_arrivals") return "newest";
  if (s === "price_asc") return "price_asc";
  if (s === "price_desc") return "price_desc";
  if (s === "name" || s === "name_asc") return "name_asc";
  if (s === "brand") return "brand";
  if (s === "stock" || s === "stock_first") return "stock_first";
  return "recommended";
}

export function catalogSortOptions(lang: StoreV2Lang): { key: CatalogSortKey; label: string }[] {
  return lang === "en"
    ? [
        { key: "recommended", label: "Recommended" },
        { key: "newest", label: "New arrivals" },
        { key: "price_asc", label: "Price ↑" },
        { key: "price_desc", label: "Price ↓" },
        { key: "name_asc", label: "Name A-Z" },
        { key: "brand", label: "Brand" },
        { key: "stock_first", label: "In stock first" },
      ]
    : [
        { key: "recommended", label: "За замовчуванням" },
        { key: "newest", label: "Нові надходження" },
        { key: "price_asc", label: "Дешевші спочатку" },
        { key: "price_desc", label: "Дорожчі спочатку" },
        { key: "name_asc", label: "Назва А-Я" },
        { key: "brand", label: "Бренд" },
        { key: "stock_first", label: "В наявності спочатку" },
      ];
}

const uk = new Intl.Collator("uk", { sensitivity: "base", numeric: true });

function stockRank(p: CatalogV2Product): number {
  if (p.stockStatus === "in_stock") return 0;
  if (p.stockStatus === "low_stock") return 1;
  if (p.isSellable) return 2;
  return 3;
}

function sellRank(p: CatalogV2Product): number {
  return p.isSellable ? 0 : p.isPriced ? 1 : 2;
}

/**
 * Canonical arrival timestamp for "newest".
 * Prefer first-seen style fields; NEVER use lastSyncedAt / sourceUpdatedAt /
 * priceUpdatedAt (routine sync or price edits must not bump "new").
 */
export function catalogArrivedAtMs(p: CatalogV2Product): number {
  const candidates = [p.catalogArrivedAt, p.firstSeenAt];
  for (const c of candidates) {
    if (!c) continue;
    const t = Date.parse(String(c));
    if (Number.isFinite(t) && t > 0) return t;
  }
  // CUSTOMER-172A — do NOT invent newness from Prom id digits (was ~1970 epoch noise).
  // Unknown → 0 → isWithinNewArrivalWindow = false.
  return 0;
}

export function sortCatalogProducts(
  products: CatalogV2Product[],
  key: CatalogSortKey | LegacySortKey,
  titleOf: (p: CatalogV2Product) => string,
): CatalogV2Product[] {
  const sortKey = normalizeCatalogSortKey(key);
  const list = [...products];
  switch (sortKey) {
    case "newest":
      return list.sort((a, b) => {
        const tb = catalogArrivedAtMs(b) - catalogArrivedAtMs(a);
        if (tb !== 0) return tb;
        return String(b.id).localeCompare(String(a.id));
      });
    case "price_asc":
      return list.sort((a, b) => {
        const d =
          (a.priceUah ?? Number.POSITIVE_INFINITY) - (b.priceUah ?? Number.POSITIVE_INFINITY);
        if (d !== 0) return d;
        return String(a.id).localeCompare(String(b.id));
      });
    case "price_desc":
      return list.sort((a, b) => {
        const d =
          (b.priceUah ?? Number.NEGATIVE_INFINITY) - (a.priceUah ?? Number.NEGATIVE_INFINITY);
        if (d !== 0) return d;
        return String(a.id).localeCompare(String(b.id));
      });
    case "name_asc":
      return list.sort((a, b) => uk.compare(titleOf(a), titleOf(b)));
    case "brand":
      return list.sort((a, b) => {
        const ba = a.brand ?? "\uffff";
        const bb = b.brand ?? "\uffff";
        return uk.compare(ba, bb) || uk.compare(titleOf(a), titleOf(b));
      });
    case "stock_first":
      return list.sort((a, b) => stockRank(a) - stockRank(b) || uk.compare(titleOf(a), titleOf(b)));
    case "recommended":
    default:
      return list.sort((a, b) => sellRank(a) - sellRank(b) || String(a.id).localeCompare(String(b.id)));
  }
}
